import classNames from 'classnames';
import { treemap as d3Treemap, hierarchy, treemapSquarify } from 'd3';
import { scaleOrdinal } from 'd3-scale';
import { truncate } from 'lodash';
import { Fragment, type KeyboardEvent, useState } from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getFormatter,
  getTextWidth,
  TRUNCATION_ELLIPSIS,
  truncateTextWithFit,
  useChartTheme,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './treemap.module.css';

export type TreeMapNode = {
  name: string;
  value: number;
  children?: TreeMapNode[];
  color?: string;
  parentName?: string;
  id?: string;
  share?: number | null;
};

export type TreeMapData = {
  name: string;
  children: TreeMapNode[];
};

type TreeDatum = {
  name: string;
  value?: number;
  color?: string;
  parentName?: string;
  children?: TreeDatum[];
  id?: string;
  share?: number | null;
};

export type BreadcrumbSegment = {
  name: string;
  targetDepth: number;
};

export type TreeMapProps = {
  data: TreeMapData;
  colorPalette?: string[];
  colorOverride?: Record<string, string>;
  onCellClick?: (
    cellId: string,
    cellName: string,
    parentName?: string,
    color?: string
  ) => void;
  onHeaderClick?: (groupId: string, groupName: string, color?: string) => void;
  onBreadcrumbClick?: (targetDepth: number) => void;
  breadcrumbPath?: BreadcrumbSegment[];

  metricLabel?: string;
  sizeTransform?: 'none' | 'sqrt';
  /** Bias cells toward wider-than-tall rectangles (still squarified). */
  horizontal?: boolean;
  enclosed?: boolean;
} & CommonPlotProps;

const HORIZONTAL_STRETCH = 1.6;
const SINGLE_GROUP_HEADER_COLOR = 'var(--ui-text-normal, #2d426a)';
const LABEL_PADDING_X = 14;
const NESTED_OTHER_LABEL = TRUNCATION_ELLIPSIS;

const TRAY_HEADER_HEIGHT = 22;
// Cells lay out flush (gap 0) so sibling 1px borders merge into one line; nested
// children are inset by TRAY_EDGE_PADDING so groups read as nested rectangles.
const TRAY_EDGE_PADDING = 5;
const ENCLOSED_INNER_GAP = 0;
const LEGACY_INNER_GAP = 2;
// Gap between differently-coloured top-level boxes.
const TOP_LEVEL_GAP = 2;

const ROOT_HEADER_HEIGHT = 22;
const ROOT_EDGE_PADDING = 6;

function getOverrideColor(
  name: string,
  colorOverride: Record<string, string> | undefined
): string | undefined {
  if (colorOverride === undefined) return undefined;
  const key = Object.keys(colorOverride).find(
    (k) => k.toLowerCase() === name.toLowerCase()
  );
  return key !== undefined ? colorOverride[key] : undefined;
}

function isOtherDatum(datum: { id?: string; name?: string }): boolean {
  if (datum.id === '-1') return true;
  return (datum.name ?? '').toLowerCase() === 'other';
}

/**
 * Whether a cell fill is dark enough to need light label text (per-cell
 * luminance, theme-independent). Non-hex fills are treated as light.
 */
function isDarkFill(fill: string): boolean {
  const hex = /^#?([0-9a-f]{6})$/i.exec(fill.trim())?.[1];
  if (hex === undefined) return false;
  const int = Number.parseInt(hex, 16);
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = channel((int >> 16) & 0xff);
  const g = channel((int >> 8) & 0xff);
  const b = channel(int & 0xff);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // Only flip to light text on genuinely dark fills, not mid-tone categoricals.
  return luminance < 0.24;
}

export const TreeMap = ({
  data,
  loading = false,
  format = FormatType.SI,
  sizeTransform = 'none',
  colorPalette: colorPaletteProp,
  colorOverride,
  onCellClick,
  onHeaderClick,
  onBreadcrumbClick,
  breadcrumbPath,
  horizontal = false,
  enclosed = false,
}: TreeMapProps) => {
  const clickable = onCellClick !== undefined;
  const headerClickable = onHeaderClick !== undefined;
  const { breakdownPalette } = useChartTheme();
  const overridenColors = Object.values(colorOverride ?? {});
  const basePalette = colorPaletteProp?.length
    ? colorPaletteProp
    : breakdownPalette;
  const colorPalette = basePalette.filter(
    (color) => !overridenColors.includes(color)
  );
  const { containerRef, width, height } = useResizeObserver();

  const isPlotSizeValid = width > 0 && height > 0;
  const hasData = Array.isArray(data?.children) && data.children.length > 0;

  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  const displayData: TreeMapData = data;
  const topLevelGroupCount = displayData.children.filter(
    (c) => Array.isArray(c.children) && c.children.length > 0
  ).length;
  const isSingleNodeTerminal = topLevelGroupCount === 1 && !clickable;
  const revealOnHover =
    enclosed && topLevelGroupCount > 0 && !isSingleNodeTerminal;

  function toTreeDatum(node: TreeMapNode): TreeDatum {
    const children = Array.isArray(node.children)
      ? node.children.map((child) => toTreeDatum(child))
      : undefined;
    return {
      name: node.name,
      value: typeof node.value === 'number' ? node.value : undefined,
      color: node.color,
      parentName: node.parentName,
      children,
      id: node.id,
      share: node.share,
    };
  }

  const rootDatum: TreeDatum = {
    name: displayData.name,
    children: displayData.children.map((c) => toTreeDatum(c)),
  };

  const root = hierarchy<TreeDatum>(rootDatum)
    .sum((d) =>
      d.children && d.children.length ? 0 : Math.max(0, d.value ?? 0)
    )
    .sort((a, b) => {
      const aIsOther = isOtherDatum(a.data);
      const bIsOther = isOtherDatum(b.data);
      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;
      return (b.value ?? 0) - (a.value ?? 0);
    });

  if (sizeTransform === 'sqrt') {
    // Per-level sqrt: re-weight each node ∝ √(its aggregate) among siblings so
    // group size doesn't inflate with child count. `.data.value` (raw metric,
    // used for labels) is untouched.
    root.eachBefore((node) => {
      const children = node.children;
      if (children === undefined || children.length === 0) return;
      let sqrtSum = 0;
      for (const child of children) {
        sqrtSum += Math.sqrt(Math.max(0, child.value ?? 0));
      }
      const budget = node.value ?? 0;
      const scale = sqrtSum > 0 ? budget / sqrtSum : 0;
      for (const child of children) {
        // d3 types `value` read-only; re-weight it deliberately.
        (child as { value?: number }).value =
          Math.sqrt(Math.max(0, child.value ?? 0)) * scale;
      }
    });
  }

  const headerHeight = 19;
  const gutterHeight = 2;
  const headerAreaHeight = headerHeight + gutterHeight;

  const drilledCrumbs = breadcrumbPath ?? [];
  const hasDrilled = drilledCrumbs.length > 0;
  const showCrumbBar = hasDrilled;
  const crumbs: BreadcrumbSegment[] = drilledCrumbs;

  const useRootTray = enclosed && showCrumbBar;
  const breadcrumbOffset = showCrumbBar && !useRootTray ? headerAreaHeight : 0;
  const treemapHeight = Math.max(0, height - breadcrumbOffset);

  const hideGroupHeaders =
    !revealOnHover && hasDrilled && topLevelGroupCount === 1;

  const useTrayLayout = enclosed && !hideGroupHeaders && topLevelGroupCount > 0;
  const isGroupNode = (node: {
    children?: unknown[];
    depth: number;
  }): boolean =>
    Array.isArray(node.children) &&
    node.children.length > 0 &&
    node.depth > 0 &&
    !hideGroupHeaders;

  // `horizontal` lays out in a vertically-stretched space, then compresses y back.
  const stretch = horizontal ? HORIZONTAL_STRETCH : 1;
  const treemapRoot = d3Treemap<TreeDatum>()
    .tile(horizontal ? treemapSquarify.ratio(1) : treemapSquarify)
    .size([Math.max(0, width), Math.max(0, treemapHeight * stretch)])
    .paddingInner((node) => {
      if (!enclosed) return LEGACY_INNER_GAP;
      // Differently-coloured cells (top-level groups, or the flat terminal drill)
      // get a wider gap; same-coloured nested cells stay flush (border-delineated).
      if (node.depth === 0 || hideGroupHeaders) return TOP_LEVEL_GAP;
      return ENCLOSED_INNER_GAP;
    })
    .paddingTop((node) => {
      if (node.depth === 0) {
        return useRootTray ? ROOT_HEADER_HEIGHT * stretch : 0;
      }
      if (!isGroupNode(node)) return 0;
      const headerPx = useTrayLayout
        ? TRAY_HEADER_HEIGHT
        : headerAreaHeight + 2;
      return headerPx * stretch;
    })
    .paddingLeft((node) => {
      if (node.depth === 0) return useRootTray ? ROOT_EDGE_PADDING : 0;
      return useTrayLayout && isGroupNode(node) ? TRAY_EDGE_PADDING : 0;
    })
    .paddingRight((node) => {
      if (node.depth === 0) return useRootTray ? ROOT_EDGE_PADDING : 0;
      return useTrayLayout && isGroupNode(node) ? TRAY_EDGE_PADDING : 0;
    })
    .paddingBottom((node) => {
      if (node.depth === 0) {
        return useRootTray ? ROOT_EDGE_PADDING * stretch : 0;
      }
      return useTrayLayout && isGroupNode(node)
        ? TRAY_EDGE_PADDING * stretch
        : 0;
    })
    .round(true)(root);
  if (stretch !== 1) {
    treemapRoot.each((n) => {
      n.y0 /= stretch;
      n.y1 /= stretch;
    });
  }

  const innerGapPx = enclosed ? ENCLOSED_INNER_GAP : LEGACY_INNER_GAP;
  const gutterInset = stretch === 1 ? 0 : (innerGapPx * (1 - 1 / stretch)) / 2;

  const leaves = treemapRoot.leaves();
  const totalRawSum = leaves.reduce((sum, leaf) => {
    const rawValue = typeof leaf.data.value === 'number' ? leaf.data.value : 0;
    return sum + rawValue;
  }, 0);
  const groupsWithChildren = hideGroupHeaders
    ? []
    : treemapRoot
        .descendants()
        .filter(
          (n) =>
            n.depth > 0 && Array.isArray(n.children) && n.children.length > 0
        );

  // Colour domain follows the value-sorted layout order (largest first, "Other"
  // last) so colours stay stable regardless of raw input order.
  const sortedTopLevelNames = treemapRoot.children
    ? treemapRoot.children.map((n) => n.data.name)
    : displayData.children.map((c) => c.name);
  const leafNames = leaves.map((l) => l.data.name);

  const colorByLeaf =
    !revealOnHover && topLevelGroupCount <= 1 && leaves.length > 1;
  const colorDomain = colorByLeaf
    ? [...new Set(leafNames)]
    : [...new Set(sortedTopLevelNames)];
  const colorScale = scaleOrdinal<string, string>()
    .domain(colorDomain)
    .range(colorPalette);

  const useSingleColor = colorPalette.length === 1;
  const singleColor = colorPalette[0];

  const resolveColor = (name: string): string => {
    const override = getOverrideColor(name, colorOverride);
    if (override !== undefined) return override;
    if (useSingleColor && singleColor !== undefined) return singleColor;
    return colorScale(name);
  };

  const formatValue = getFormatter(format);
  const formatShare = getFormatter(FormatType.PERCENTAGE);

  const toDisplayValue = (rawValue: number): number => {
    if (format === FormatType.PERCENTAGE) {
      return totalRawSum === 0 ? 0 : rawValue / totalRawSum;
    }
    return rawValue;
  };

  const measureBreadcrumb = (text: string): number =>
    getTextWidth({ text, fontSize: 12, fontWeight: 400 }) ?? text.length * 6;
  const breadcrumbHeadLabels = crumbs
    .slice(0, -1)
    .map((c) => truncate(c.name, { length: 50 }));
  const breadcrumbLastLabel = (() => {
    const last = crumbs[crumbs.length - 1];
    if (last === undefined) return '';
    const sepWidth = crumbs.length > 1 ? measureBreadcrumb('   ›   ') : 0;
    const totalWidth = useRootTray
      ? measureBreadcrumb(`    ${formatValue(toDisplayValue(totalRawSum))}`)
      : 0;
    const prefixWidth =
      breadcrumbHeadLabels.reduce((w, t) => w + measureBreadcrumb(t), 0) +
      (crumbs.length - 1) * sepWidth +
      totalWidth;
    const maxWidth = Math.max(0, width - 16 - prefixWidth);
    return truncateTextWithFit({
      text: last.name,
      maxWidth,
      fontSize: 12,
      fontWeight: 400,
    }).text;
  })();
  const breadcrumbLabels = [...breadcrumbHeadLabels, breadcrumbLastLabel];

  return (
    <div
      className={classNames(styles.container)}
      data-testid={plotTestId(ReplotChartNames.TreeMap)}
      onMouseLeave={revealOnHover ? () => setHoveredGroupId(null) : undefined}
    >
      <div ref={containerRef} className={styles.sizingBox}>
        <PlotLoadWrapper loading={loading} noData={!hasData}>
          <svg width="100%" height="100%">
            {isPlotSizeValid && (
              <g>
                {showCrumbBar && (
                  <g>
                    {useRootTray ? (
                      <rect
                        x={0}
                        y={0}
                        width={width}
                        height={height}
                        className={styles.rootTrayRect}
                      />
                    ) : (
                      <rect
                        x={0}
                        y={0}
                        width={width}
                        height={breadcrumbOffset - innerGapPx}
                        className={styles.breadcrumbBar}
                      />
                    )}
                    <text
                      x={8}
                      y={
                        useRootTray
                          ? 14
                          : (breadcrumbOffset - innerGapPx) / 2 + 4.2
                      }
                      className={classNames(
                        styles.breadcrumbLabel,
                        !useRootTray && styles.breadcrumbBarText
                      )}
                    >
                      {crumbs.map((seg, i) => {
                        const handleClick = () => {
                          onBreadcrumbClick?.(seg.targetDepth);
                        };
                        const handleKeyDown = (e: KeyboardEvent<Element>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick();
                          }
                        };
                        return (
                          <Fragment key={`breadcrumb-${seg.targetDepth}`}>
                            {i > 0 && (
                              <tspan className={styles.breadcrumbSeparator}>
                                &nbsp;&nbsp;&nbsp;›&nbsp;&nbsp;&nbsp;
                              </tspan>
                            )}
                            <tspan
                              data-testid={`treemap-breadcrumb-${seg.targetDepth}`}
                              onClick={handleClick}
                              onKeyDown={handleKeyDown}
                              role="button"
                              tabIndex={0}
                              className={styles.breadcrumbLink}
                              style={{ cursor: 'pointer' }}
                            >
                              {breadcrumbLabels[i]}
                            </tspan>
                          </Fragment>
                        );
                      })}
                      {useRootTray && (
                        <tspan className={styles.breadcrumbTotal}>
                          {`    ${formatValue(toDisplayValue(totalRawSum))}`}
                        </tspan>
                      )}
                    </text>
                  </g>
                )}
                {groupsWithChildren.map((groupNode) => {
                  const trayTop = groupNode.y0 + breadcrumbOffset;
                  const rawW = groupNode.x1 - groupNode.x0;
                  const x = Math.max(0, groupNode.x0 - gutterInset);
                  const y = useTrayLayout
                    ? trayTop
                    : groupNode.y0 + 2 + breadcrumbOffset;
                  const w = Math.min(width, groupNode.x1 + gutterInset) - x;
                  const h = useTrayLayout
                    ? Math.max(0, groupNode.y1 - groupNode.y0)
                    : headerHeight;
                  if (rawW <= 0 || h <= 0) return null;

                  const title = String(groupNode.data.name ?? '');
                  const groupTotal = groupNode
                    .leaves()
                    .reduce(
                      (s, l) =>
                        s +
                        (typeof l.data.value === 'number' ? l.data.value : 0),
                      0
                    );
                  const groupShare = groupNode
                    .leaves()
                    .reduce<number | null>(
                      (s, l) =>
                        s === null || typeof l.data.share !== 'number'
                          ? null
                          : s + l.data.share,
                      0
                    );
                  const valueLabel = formatValue(toDisplayValue(groupTotal));
                  const headerLabel = truncateTextWithFit({
                    text: title,
                    maxWidth: Math.max(0, w - LABEL_PADDING_X),
                    fontSize: 12,
                    fontWeight: 500,
                  });
                  const headerLabelShown =
                    w > 40 && h >= 16 && headerLabel.fitted;
                  const headerValueText =
                    groupShare === null
                      ? valueLabel
                      : `${valueLabel} / ${formatShare(groupShare)}`;
                  const headerTitleHidden =
                    !headerLabelShown || headerLabel.text !== title;
                  const groupId = String(groupNode.data.id ?? title);
                  // Collapsed group shows its total under the label (children's
                  // values are hidden until it expands).
                  const isGroupExpanded = hoveredGroupId !== null;
                  const showHeaderValue =
                    revealOnHover && !isGroupExpanded && w > 40 && h >= 34;

                  const fill = colorByLeaf
                    ? SINGLE_GROUP_HEADER_COLOR
                    : resolveColor(title);
                  // Measure the concrete navy so the label picks light text.
                  const headerOnDark = isDarkFill(
                    colorByLeaf ? '#2d426a' : fill
                  );

                  const handleHeaderClick = () => {
                    onHeaderClick?.(
                      groupId,
                      title,
                      colorByLeaf ? undefined : fill
                    );
                  };
                  const handleHeaderKeyDown = (e: KeyboardEvent<Element>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleHeaderClick();
                    }
                  };

                  return (
                    <g
                      key={`header-${groupId}-${groupNode.x0}-${groupNode.y0}`}
                    >
                      <rect
                        data-testid={`treemap-header-${groupId}`}
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        fill={fill}
                        className={classNames(
                          useTrayLayout ? styles.trayRect : styles.headerRect,
                          enclosed && styles.bordered
                        )}
                        style={headerClickable ? { cursor: 'pointer' } : {}}
                        onMouseEnter={
                          revealOnHover
                            ? () => setHoveredGroupId(groupId)
                            : undefined
                        }
                        onClick={
                          headerClickable ? handleHeaderClick : undefined
                        }
                        onKeyDown={
                          headerClickable ? handleHeaderKeyDown : undefined
                        }
                        role={headerClickable ? 'button' : undefined}
                        tabIndex={headerClickable ? 0 : undefined}
                      >
                        {headerTitleHidden && (
                          <title>{`${title} · ${headerValueText}`}</title>
                        )}
                      </rect>
                      {headerLabelShown && (
                        <text
                          x={x + (useTrayLayout ? 8 : 6)}
                          y={useTrayLayout ? trayTop + 16 : y + 13}
                          className={classNames(
                            styles.headerLabel,
                            headerOnDark
                              ? styles.labelOnDarkCell
                              : styles.labelOnLightCell
                          )}
                        >
                          {headerLabel.text}
                        </text>
                      )}
                      {showHeaderValue && (
                        <text
                          x={x + (useTrayLayout ? 8 : 6)}
                          y={useTrayLayout ? trayTop + 30 : y + 25}
                          className={classNames(
                            styles.nodeValueLabel,
                            headerOnDark
                              ? styles.labelOnDarkCell
                              : styles.labelOnLightCell
                          )}
                        >
                          {valueLabel}
                        </text>
                      )}
                    </g>
                  );
                })}
                {leaves.map((leaf, idx) => {
                  const standaloneTopInset =
                    !useTrayLayout &&
                    leaf.depth === 1 &&
                    groupsWithChildren.length > 0
                      ? 2
                      : 0;
                  const rawW = leaf.x1 - leaf.x0;
                  const rawH = leaf.y1 - leaf.y0 - standaloneTopInset;
                  const x = Math.max(0, leaf.x0 - gutterInset);
                  const y = leaf.y0 + breadcrumbOffset + standaloneTopInset;
                  const w = Math.min(width, leaf.x1 + gutterInset) - x;
                  const h = Math.max(0, rawH);
                  if (rawW <= 0 || rawH <= 0) return null;

                  let group = leaf;
                  while (group.parent && group.parent.depth > 0) {
                    group = group.parent;
                  }
                  const groupName = group.data.name;
                  // Reveal-all: nested children (depth > 1) render only while
                  // something is hovered; top-level leaves always render.
                  if (
                    revealOnHover &&
                    leaf.depth > 1 &&
                    hoveredGroupId === null
                  ) {
                    return null;
                  }
                  const colorKey = colorByLeaf ? leaf.data.name : groupName;
                  const isOther = isOtherDatum(leaf.data);
                  const isNestedOther = isOther && leaf.depth > 1;
                  const drillsToParent = isNestedOther && headerClickable;
                  const parentColor = (() => {
                    if (useSingleColor) return singleColor;
                    if (colorByLeaf) return undefined;
                    return colorScale(groupName);
                  })();
                  const fill = (() => {
                    if (leaf.data.color) return leaf.data.color;
                    // Nesting is shown by borders, not shading: children take the
                    // group colour; "Other" keeps the neutral Other colour.
                    if (enclosed && drillsToParent) {
                      return parentColor ?? resolveColor(groupName);
                    }
                    if (enclosed && isOther) {
                      return (
                        getOverrideColor('Other', colorOverride) ??
                        resolveColor(String(leaf.data.name ?? ''))
                      );
                    }
                    if (useTrayLayout && leaf.depth > 1) {
                      return parentColor ?? resolveColor(colorKey);
                    }
                    if (isNestedOther && parentColor !== undefined) {
                      return parentColor;
                    }
                    return resolveColor(colorKey);
                  })();
                  const labelOnDark = isDarkFill(fill);

                  const boldLeaf =
                    enclosed &&
                    !drillsToParent &&
                    !colorByLeaf &&
                    !(useTrayLayout && leaf.depth > 1) &&
                    (isOther ||
                      !clickable ||
                      (revealOnHover && leaf.depth === 1));

                  const name = String(leaf.data.name ?? '');
                  const displayName = drillsToParent
                    ? NESTED_OTHER_LABEL
                    : name;
                  const parentName = String(
                    leaf.data.parentName ?? leaf.parent?.data.name ?? ''
                  );
                  const displayNumericValue =
                    typeof leaf.data.value !== 'number'
                      ? undefined
                      : toDisplayValue(leaf.data.value);
                  const valueLabel =
                    displayNumericValue === undefined
                      ? '-'
                      : formatValue(displayNumericValue);
                  const shareValue =
                    typeof leaf.data.share === 'number'
                      ? leaf.data.share
                      : undefined;
                  const cellLabel = truncateTextWithFit({
                    text: displayName,
                    maxWidth: Math.max(0, w - LABEL_PADDING_X),
                    fontSize: 12,
                  });
                  const showCellName = w > 40 && h >= 16 && cellLabel.fitted;
                  const showCellValue = w > 40 && h >= 34;
                  const cellNameY = showCellValue
                    ? y + 16
                    : y + Math.round(h / 2) + 3;
                  const cellValueText =
                    shareValue === undefined
                      ? valueLabel
                      : `${valueLabel} / ${formatShare(shareValue)}`;
                  const cellTitleHidden =
                    !showCellName ||
                    cellLabel.text !== displayName ||
                    isNestedOther;
                  const cellTipTitle = isNestedOther ? name : displayName;

                  const cellId = String(leaf.data.id ?? name);
                  const groupId = String(group.data.id ?? groupName);
                  const interactive = drillsToParent || clickable;
                  const handleClick = () => {
                    if (drillsToParent) {
                      onHeaderClick?.(
                        groupId,
                        groupName,
                        colorByLeaf ? undefined : parentColor
                      );
                      return;
                    }
                    if (clickable) {
                      onCellClick?.(
                        cellId,
                        name,
                        parentName || undefined,
                        fill
                      );
                    }
                  };
                  const handleKeyDown = (e: KeyboardEvent<Element>) => {
                    if (interactive && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleClick();
                    }
                  };

                  return (
                    <g key={`${name}-${idx}`}>
                      <rect
                        data-testid={`treemap-cell-${cellId}`}
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        fill={fill}
                        className={classNames(
                          (useRootTray && !useTrayLayout) ||
                            (useTrayLayout &&
                              isOtherDatum(leaf.data) &&
                              !isNestedOther)
                            ? styles.trayRect
                            : styles.nodeRect,
                          enclosed && styles.bordered
                        )}
                        onMouseEnter={
                          revealOnHover
                            ? () => setHoveredGroupId(groupId)
                            : undefined
                        }
                        onClick={interactive ? handleClick : undefined}
                        onKeyDown={interactive ? handleKeyDown : undefined}
                        style={interactive ? { cursor: 'pointer' } : undefined}
                        role={interactive ? 'button' : undefined}
                        tabIndex={interactive ? 0 : undefined}
                      >
                        {cellTitleHidden && (
                          <title>{`${cellTipTitle} · ${cellValueText}`}</title>
                        )}
                      </rect>
                      {(showCellName || showCellValue) && (
                        <g>
                          {showCellName && (
                            <text
                              x={x + 8}
                              y={cellNameY}
                              className={classNames(
                                boldLeaf
                                  ? styles.nodeLabelBold
                                  : styles.nodeLabel,
                                labelOnDark
                                  ? styles.labelOnDarkCell
                                  : styles.labelOnLightCell
                              )}
                            >
                              {cellLabel.text}
                            </text>
                          )}
                          {showCellValue && (
                            <text
                              x={x + 8}
                              y={y + 30}
                              className={classNames(
                                styles.nodeValueLabel,
                                labelOnDark
                                  ? styles.labelOnDarkCell
                                  : styles.labelOnLightCell
                              )}
                            >
                              {valueLabel}
                            </text>
                          )}
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            )}
          </svg>
        </PlotLoadWrapper>
      </div>
    </div>
  );
};
